import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Presentation.css';

const bg = (src) => ({
  backgroundImage: `url('${src}')`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

function LogoSVG({ color = 'currentColor', width = 80, height = 72 }) {
  return (
    <svg viewBox="0 0 80 72" style={{ color, width, height }} fill="none" strokeLinejoin="miter">
      <rect x="30" y="6" width="20" height="54" fill="#C8CDD4" />
      <rect x="36" y="14" width="8" height="16" fill="#0F0F0F" />
      <path d="M8 60 L8 32 L26 22 L26 60" stroke="currentColor" strokeWidth="1.5" />
      <path d="M46 60 L46 36 L60 24 L74 36 L74 60" stroke="currentColor" strokeWidth="1.5" />
      <line x1="4" y1="60" x2="78" y2="60" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function Brand({ color = '#F5F1EA', size = 80 }) {
  return (
    <div className="brand" style={{ alignItems: 'flex-start' }}>
      <LogoSVG color={color} width={size} height={size * 0.9} />
      <div style={{ marginTop: '6px' }}>
        <div className="name">IBR PERÚ</div>
        <div className="sub">BIENES RAÍCES</div>
      </div>
    </div>
  );
}

// Tell React this is a custom HTML element
const DeckStage = 'deck-stage';

export default function Presentation() {
  useEffect(() => {
    // Always start at slide 1: drop the saved position and reset the deck
    // if the <deck-stage> element was already upgraded from a prior visit.
    localStorage.removeItem(`deck-stage:slide:${window.location.pathname}`);
    document.querySelector('deck-stage')?.reset?.();

    const script = document.createElement('script');
    script.src = '/deck-stage.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  return (
    <>
      {/* Back button — floats above the deck */}
      <Link to="/" className="pres-back">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M10 3L5 8l5 5" />
        </svg>
        Volver al sitio
      </Link>

      <div className="pres-root">
        <DeckStage>

          {/* ══════════════════════════════════════════
              SLIDE 1 — PORTADA
          ══════════════════════════════════════════ */}
          <section className="slide s1" data-screen-label="01 Portada">
            <div className="left">
              <Brand />
              <div>
                <div className="eyebrow">Condominio's Resort</div>
                <h1 className="display" style={{ marginTop: 24, fontFamily: '"Cormorant Garamond", serif' }}>
                  "El Alto<br />
                  <em style={{ fontStyle: 'italic', color: 'var(--gold)', fontWeight: 300 }}>de los</em><br />
                  Bosques"
                </h1>
              </div>
              <div className="meta" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Preventa 2026</span><span>· 01 / 10</span>
              </div>
            </div>
            <div className="right">
              <div style={{ position: 'absolute', inset: 0, ...bg('/assets/slide1-hero.jpg') }} />
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 2 — UBICACIÓN
          ══════════════════════════════════════════ */}
          <section className="slide s2" data-screen-label="02 Ubicación">
            <div className="ph-full" style={bg('/assets/slide2-banos.jpg')} />
            <div className="overlay" />
            <div className="content">
              <h2 style={{ marginTop: 40, fontFamily: '"Cormorant Garamond", serif' }}>
                <span className="quote">"</span>El Alto<br />de los Bosques<span className="quote">"</span>.
              </h2>
              <div className="lead">Tu paraíso privado en una de las maravillas del Perú.</div>
            </div>
            <div className="pin">
              <span className="dot" />
              <span>Baños del Inca · Cajamarca · Perú</span>
            </div>
            <div className="s-footer">
              <span>IBR PERÚ · BIENES RAÍCES</span>
              <span>02 / 10</span>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 3 — CONCEPTO
          ══════════════════════════════════════════ */}
          <section className="slide s3" data-screen-label="03 Concepto">
            <div className="two-col">
              <div className="text">
                <p>
                  <b>"El Alto de los Bosques"</b> es un exclusivo proyecto inmobiliario que ofrece{' '}
                  <b>casas de campo en un ambiente natural de lujo</b> en preventa, pensados para quienes
                  buscan disfrutar del paraíso, la naturaleza, el confort y la exclusividad en una nueva forma de vivir.
                </p>
                <p>Los propietarios podrán generar ingresos a través del alquiler turístico.</p>
                <p>
                  Ubicada estratégicamente en una de las maravillas del Perú,{' '}
                  <b>Baños del Inca. "El Alto de los Bosques"</b>, combina diseño moderno, confort premium y una
                  ubicación privilegiada para brindar una experiencia única tanto a propietarios como a visitantes.
                </p>
              </div>
              <div className="ph-wrap">
                <div style={{ width: '100%', height: '100%', ...bg('/assets/slide3-concepto.jpg') }} />
              </div>
            </div>
            <div className="s-footer">
              <span>EL CONCEPTO</span>
              <span>03 / 10</span>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 4 — AMENIDADES
          ══════════════════════════════════════════ */}
          <section className="slide s4" data-screen-label="04 Amenidades">
            <div className="gallery">
              <div className="ph ga" style={bg('/assets/slide4-area-verde.jpg')} />
              <div className="ph gb" style={bg('/assets/slide4-spa.jpg')} />
              <div className="ph gc" style={bg('/assets/slide4-parrillas.jpg')} />
              <div className="ph gd" style={bg('/assets/slide4-cancha.jpg')} />
            </div>
            <div className="list">
              <h2 style={{ marginTop: 28 }}>"El Alto de los Bosques"<br />ofrece acceso a:</h2>
              <div className="am-grid">
                {[
                  ['01', 'Convivencia natural y privada.'],
                  ['02', 'Zona social.'],
                  ['03', 'Zona recreativa para niños.'],
                  ['04', 'Área de deportes, tenis, fútbol, voleibol.'],
                  ['05', 'Restaurant y Minimarket.'],
                  ['06', 'Recorrido abierto en el proyecto.'],
                  ['07', 'Experiencia en la naturaleza.'],
                  ['08', 'Tiempo de llegada al proyecto (14 minutos).'],
                ].map(([n, t]) => (
                  <div className="am-item" key={n}>
                    <div className="n">{n}</div>
                    <div className="t">{t}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="s-footer" style={{ left: 48, right: 48 }}>
              <span>AMENIDADES</span>
              <span>04 / 10</span>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 5 — ESPECIFICACIONES
          ══════════════════════════════════════════ */}
          <section className="slide s5" data-screen-label="05 Especificaciones">
            <div className="text">
              <h2>
                "El Alto de los Bosques" es un proyecto moderno que se envuelve con el campo natural,
                diseñada para aprovechar la belleza del paisaje{' '}
                <em style={{ fontStyle: 'italic', color: 'var(--gold)' }}>Cajamarquino</em> y de su entorno, ofreciendo:
              </h2>
              <div className="spec-table">
                {[
                  ['01', 'Casas de 1 y 2 niveles.'],
                  ['02', 'Cada casa estará en un terreno de 410 m².'],
                  ['03', '10 casas de 3 recámaras.'],
                  ['04', 'Balcones con vista al paisajismo del proyecto.'],
                  ['05', 'Estacionamiento privado.'],
                  ['06', 'Condominio de lujo con todos los servicios.'],
                ].map(([k, v]) => (
                  <div className="row" key={k}>
                    <div className="k">{k}</div>
                    <div className="v">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="visual">
              <div className="stack">
                <div className="ph big" style={bg('/assets/slide5-new.jpg')} />
                <div className="ph" style={bg('/assets/slide5-a.jpg')} />
                <div className="ph" style={bg('/assets/slide5-c.jpg')} />
              </div>
            </div>
            <div className="s-footer">
              <span>LA RESIDENCIA</span>
              <span>05 / 10</span>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 6 — POR QUÉ ELEGIRLO
          ══════════════════════════════════════════ */}
          <section className="slide s6" data-screen-label="06 Por qué elegirlo">
            <div className="ph-full" style={bg('/assets/slide6-hero.jpg')} />
            <div className="overlay" />
            <div className="inner">
              <div className="kicker-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="eyebrow">La decisión</div>
                <div className="mono" style={{ color: 'var(--muted)', fontSize: 12, letterSpacing: '.24em' }}>06 / 10</div>
              </div>
              <h2 style={{ alignSelf: 'flex-start', marginTop: 24, fontFamily: '"Cormorant Garamond", serif' }}>
                ¿Por qué elegir<br />
                <em style={{ fontStyle: 'italic', color: 'var(--gold)' }}>"El alto de los Bosques"</em>?
              </h2>
              <div className="reasons">
                {[
                  ['01', 'Alquiler ó renta vacacional garantizada por ubicación natural y diseño.'],
                  ['02', 'Amenidades exclusivas: área social con parrilla, gimnasio, spa, lounge, área de comidas y deportes.'],
                  ['03', 'Diseño arquitectónico minimalista con acabados premium.'],
                  ['04', 'Alta plusvalía en zona de crecimiento sostenido.'],
                  ['05', 'Administración profesional para facilitar la operación en renta Airbnb.'],
                ].map(([n, t]) => (
                  <div className="reason" key={n}>
                    <div className="n">{n}</div>
                    <div className="t">{t}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 7 — PLAN DE PRECIOS (divisor)
          ══════════════════════════════════════════ */}
          <section className="slide s7" data-screen-label="07 Plan de precios">
            <div className="left">
              <div className="kicker-row">
                <div className="eyebrow">Sección II</div>
                <div className="no">07 / 10</div>
              </div>
              <div>
                <div className="title">
                  Plan<br />de <em>Precios</em><br />y Ventas.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <LogoSVG color="var(--gold)" width={60} height={54} />
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: '.28em', color: 'var(--muted)' }}>
                  IBR PERÚ · BIENES RAÍCES
                </div>
              </div>
            </div>
            <div className="right">
              <hr className="rule-thin" />
              <div className="meta">
                {[['Inventario','10 unidades'],['Etapa','Preventa'],['Terreno','410 m²'],['Recámaras','3 por casa']].map(([k,v]) => (
                  <div key={k}>
                    <div className="k">{k}</div>
                    <div className="v">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 8 — PRECIOS DE PREVENTA
          ══════════════════════════════════════════ */}
          <section className="slide s8" data-screen-label="08 Precios de preventa">
            <div className="inner">
              <div className="left">
                <div className="from">PRECIOS DE PREVENTA DESDE</div>
                <div className="price">
                  <span className="cur">$</span>155´000<span className="cents">.00</span>
                </div>
                <hr className="rule" style={{ marginTop: 56 }} />
              </div>
              <div className="right">
                <h3>
                  Durante la preventa, ofrecemos{' '}
                  <em style={{ fontStyle: 'italic', color: 'var(--gold)' }}><br />atractivos planes de pago</em>:
                </h3>
                <div className="pay">
                  {[
                    ['01', 'Enganche desde el 35%'],
                    ['02', 'Financiamiento directo en etapas de construcción'],
                    ['03', 'Descuentos por pago al contado'],
                    ['04', 'Opción de entrega amueblado con costo preferencial'],
                  ].map(([n, t]) => (
                    <div className="row" key={n}>
                      <div className="n">{n}</div>
                      <div className="t">{t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="s-footer">
              <span>PRECIOS &amp; PLANES</span>
              <span>08 / 10</span>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 9 — EQUIPO
          ══════════════════════════════════════════ */}
          <section className="slide s9" data-screen-label="09 Equipo">
            <div className="text">
              <div className="years">+7<span className="plus">años</span></div>
              <h2>
                Contamos con más de 7 años de experiencia desarrollando{' '}
                <em style={{ fontStyle: 'italic', color: 'var(--gold)' }}>proyectos Inmobiliarios.</em>
              </h2>
              <hr className="rule" style={{ marginTop: 48 }} />
            </div>
            <div className="right">
              <div className="k">Nuestro equipo incluye</div>
              <div className="team-list">
                {[
                  ['01', 'Arquitectos reconocidos a nivel nacional.', ''],
                  ['02', 'Ingenieros con amplia trayectoria en proyectos inmobiliarios.', ''],
                  ['03', 'Asesores legales y financieros especializados en inversiones inmobiliarias.', ''],
                ].map(([n, t]) => (
                  <div className="team-row" key={n}>
                    <div className="n">{n}</div>
                    <div><div className="t">{t}</div></div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ height: 180, ...bg('/assets/slide9-team-a.jpg') }} />
                <div style={{ height: 180, ...bg('/assets/slide9-team-b.jpg') }} />
              </div>
            </div>
            <div className="s-footer">
              <span>EL EQUIPO</span>
              <span>09 / 10</span>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              SLIDE 10 — CONTACTO
          ══════════════════════════════════════════ */}
          <section className="slide s10" data-screen-label="10 Contacto">
            <div className="left">
              <Brand />
              <div>
                <h2 style={{ marginTop: 22 }}>
                  Invierte en tu propia <em>calidad de vida</em> hoy mismo
                </h2>
              </div>
              <div>
                <div className="contact">
                  <div className="c">
                    <svg className="ico" fill="none" stroke="currentColor" strokeWidth="1.3" viewBox="0 0 24 24">
                      <rect x="3" y="5" width="18" height="14" /><path d="M3 7l9 7 9-7" />
                    </svg>
                    <div>
                      <div className="v">info@ibrperu.com</div>
                      <div className="k">E-mail</div>
                    </div>
                  </div>
                  <div className="c">
                    <svg className="ico" fill="none" stroke="currentColor" strokeWidth="1.3" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
                    </svg>
                    <div>
                      <div className="v">www.ibrperu.com</div>
                      <div className="k">Sitio web</div>
                    </div>
                  </div>
                  <div className="c">
                    <svg className="ico" fill="none" stroke="currentColor" strokeWidth="1.3" viewBox="0 0 24 24">
                      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A15 15 0 013 6a2 2 0 012-2z" />
                    </svg>
                    <div>
                      <div className="v">(+51) 965 194 210</div>
                      <div className="k">Teléfono directo</div>
                    </div>
                  </div>
                </div>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: '.24em', color: 'var(--muted)', marginTop: 48, textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Baños del Inca · Cajamarca · Perú</span>
                  <span>10 / 10</span>
                </div>
              </div>
            </div>
            <div className="right">
              <div className="ph-full" style={bg('/assets/slide10-hero.jpg')} />
            </div>
          </section>

        </DeckStage>
      </div>
    </>
  );
}
